/**
 * Shared helpers for the "PDP image set based on references" flow.
 *
 * - callOpenAiProxy: store-side equivalent of the Vue `callOpenAI` method
 *   (posts to the backend /call-open-ai proxy). Lets store handlers run
 *   GPT-4o calls without a component instance.
 * - classifyReferenceImages: label each reference image with a known PDP
 *   image type or 'other' (+ a short on-the-fly label).
 * - buildReferenceDesignPayload: emiliano's per-image analysis — given the
 *   target product image and a reference scene, returns the /networks/design
 *   payload that places the product into that scene.
 *
 * NOTE: this module must NOT import from pdp-generation-paths.js — that file
 * imports buildReferenceDesignPayload from here, so a back-import would create a
 * require cycle. The known-type catalog is passed in as a parameter instead.
 */

/**
 * Post to the backend OpenAI proxy. Mirrors NetworksPageV2.callOpenAI so the
 * same multipart contract is used (messages / functions / response_format / model).
 */
export async function callOpenAiProxy(settings = {}) {
  const formData = new FormData();
  if (Array.isArray(settings.messages)) {
    formData.append('messages', JSON.stringify(settings.messages));
  }
  if (Array.isArray(settings.functions)) {
    formData.append('functions', JSON.stringify(settings.functions));
  }
  if (settings.response_format) {
    formData.append('response_format', JSON.stringify(settings.response_format));
  }
  formData.append('model', settings.model || 'o3');

  const { data } = await axios.post('/call-open-ai', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

function parseJsonContent(response) {
  let content = response?.choices?.[0]?.message?.content || response?.content || '';
  content = String(content).replace(/```json/g, '').replace(/```/g, '').trim();
  if (!content) return null;
  try {
    return JSON.parse(content);
  } catch (e) {
    return null;
  }
}

/**
 * Convert an internal (same-origin) image URL to a base64 data URI so OpenAI
 * can read it (OpenAI cannot download our internal /share?file= URLs). Public
 * cross-origin URLs (e.g. Amazon CDN) and existing data: URIs are returned
 * unchanged — OpenAI fetches public URLs itself, and a cross-origin fetch here
 * would be blocked by CORS anyway. On any failure the original URL is returned.
 */
async function toOpenAiImageUrl(url) {
  if (!url || typeof url !== 'string' || url.startsWith('data:')) return url;

  let sameOrigin = false;
  try {
    sameOrigin = new URL(url, window.location.href).origin === window.location.origin;
  } catch (e) {
    sameOrigin = url.startsWith('/');
  }
  if (!sameOrigin) return url;

  try {
    const resp = await fetch(url);
    if (!resp.ok) return url;
    const blob = await resp.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn('Could not inline image for OpenAI; leaving URL as-is', url, e);
    return url;
  }
}

/**
 * Classify reference images into known PDP types (or 'other').
 * `knownTypes` is an array of { key, label } passed in by the caller (it owns
 * the BUILT_IN_TYPES catalog) — this keeps the module free of a require cycle.
 * Returns one entry per input URL, in the same order:
 *   [{ url, type, label }]
 * On any failure, falls back to { type: 'other', label: `Reference N` }.
 */
export async function classifyReferenceImages(urls = [], knownTypes = []) {
  const list = (urls || []).filter(Boolean);
  const fallback = () => list.map((url, i) => ({ url, type: 'other', label: `Reference ${i + 1}` }));
  if (!list.length) return [];

  const knownByKey = new Map((knownTypes || []).map((t) => [t.key, t]));
  const knownTypesPrompt = (knownTypes || [])
    .map((t) => `- ${t.key}: ${t.label}`)
    .join('\n');

  try {
    const inlinedList = await Promise.all(list.map(toOpenAiImageUrl));
    const response = await callOpenAiProxy({
      model: 'gpt-4o',
      messages: [
        {
          role: 'system',
          content: `You label Amazon-style PDP (product detail page) images by their purpose.
Known PDP image types (use the exact key):
${knownTypesPrompt}

For each image, pick the single best matching known type key. Be aggressive about
mapping to a known type — almost every PDP image fits one of the categories above:
- Panels about benefits, advantages, "what's in it", or "why it's better" → feature_benefits
- Panels with steps, instructions, "how to use/drink/apply" → how_to
- Panels with health claims, studies, awards, endorsements, or trust signals → reason_to_believe
- Nutrition labels / FDA panels → nutrition_facts
- Ingredients lists → ingredients
- Photography with people or in-use scenes / environments → lifestyle
- Plain product packshot on white or minimal background → hero

Do NOT invent free-form categories like "Superfood Benefits", "Heart Health Packs",
or "How To Drink" — those all map to one of the known types above. Only use "other"
when the image truly doesn't fit any known type (e.g. an isolated award badge, size
chart, or warranty card). For "other" entries, write a short (1-3 word) human label.

Return ONLY a JSON object: { "items": [ { "type": "<key|other>", "label": "<short label>" } ] }
with one entry per image, in the same order as provided.`,
        },
        {
          role: 'user',
          content: [
            { type: 'text', text: 'Label these PDP images in order:' },
            ...inlinedList.map((u) => ({ type: 'image_url', image_url: { url: u } })),
          ],
        },
      ],
      response_format: {
        name: 'pdp_reference_labels',
        strict: true,
        schema: {
          type: 'object',
          properties: {
            items: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  type: { type: 'string' },
                  label: { type: 'string' },
                },
                required: ['type', 'label'],
                additionalProperties: false,
              },
            },
          },
          required: ['items'],
          additionalProperties: false,
        },
      },
    });

    const data = parseJsonContent(response);
    const items = Array.isArray(data?.items) ? data.items : null;
    if (!items || !items.length) return fallback();

    return list.map((url, i) => {
      const raw = items[i] || {};
      const key = typeof raw.type === 'string' ? raw.type.trim() : 'other';
      const known = knownByKey.get(key);
      // For known types always use the canonical label — otherwise GPT-4o's
      // free-form descriptions (e.g. "Superfood Benefits", "Reason to Believe"
      // with lowercase t) leak into the row picker even though the type
      // resolved cleanly to a known key.
      const label = known
        ? known.label
        : ((raw.label && String(raw.label).trim()) || `Reference ${i + 1}`);
      return { url, type: known ? key : 'other', label };
    });
  } catch (e) {
    console.warn('classifyReferenceImages failed; labelling as Other', e);
    return fallback();
  }
}

/**
 * Ground PDP infographics in the ACTUAL product (POR-13307).
 *
 * The infographic image types (feature_benefits, reason_to_believe, ingredients,
 * how_to) instruct Gemini NOT to draw the product — only to use it as a reference
 * for WHAT to say. Combined with a weak product label (a product is stored as a
 * raw Salsify / ASIN / GTIN id, not a name), Gemini ignores the packshot and
 * invents generic, unrelated content (e.g. a smart-water-bottle feature panel for
 * an OREO pack). This reads the packshot ONCE via GPT-4o vision and returns
 * concrete, product-specific copy to inject into those prompts.
 *
 * Returns { name, feature_benefits:[{feature,benefit}], reasons_to_believe:[..],
 * ingredients:[..], how_to:[..] }, or null on any failure (callers fall back to
 * the un-grounded prompt). Not cached — the brief must reflect the current
 * product image.
 */
export async function extractProductBrief({ productUrl, productName = null } = {}) {
  if (!productUrl) return null;

  const nameHint = productName && String(productName).trim()
    ? ` The user refers to it as "${String(productName).trim()}" (this may be an internal id/code — rely on the image to identify the real product).`
    : '';

  try {
    const inlined = await toOpenAiImageUrl(productUrl);
    const response = await callOpenAiProxy({
      model: 'gpt-4o',
      messages: [
        {
          role: 'system',
          content: `You are a retail product marketing analyst. Look at the product packshot and identify the exact product (brand, product name, variant). Then produce concise, factual PDP marketing content that is SPECIFIC to THIS product — never generic and never about an unrelated product category.
Rules:
- Base everything strictly on the actual product visible in the image and its category. If unsure of a detail, keep it general to THIS product/category — never borrow specs from a different product type (do NOT mention smart sensors, LEDs, batteries, vacuum insulation, water bottles, or electronics for a food/snack item, etc.).
- feature_benefits: 3-4 real, plausible on-pack features each paired with the shopper benefit.
- reasons_to_believe: 3-4 trust signals / claims a brand like this would credibly make.
- ingredients: key ingredients ONLY if this is a food / beverage / personal-care item and they are reasonably known; otherwise return an empty array.
- how_to: 3-4 short usage steps ONLY if the product has a meaningful usage flow; otherwise an empty array.
Keep every string short (a few words).`,
        },
        {
          role: 'user',
          content: [
            { type: 'text', text: `Identify this product and return the PDP brief.${nameHint}` },
            { type: 'image_url', image_url: { url: inlined } },
          ],
        },
      ],
      response_format: {
        name: 'pdp_product_brief',
        strict: true,
        schema: {
          type: 'object',
          properties: {
            name: { type: 'string' },
            feature_benefits: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  feature: { type: 'string' },
                  benefit: { type: 'string' },
                },
                required: ['feature', 'benefit'],
                additionalProperties: false,
              },
            },
            reasons_to_believe: { type: 'array', items: { type: 'string' } },
            ingredients: { type: 'array', items: { type: 'string' } },
            how_to: { type: 'array', items: { type: 'string' } },
          },
          required: ['name', 'feature_benefits', 'reasons_to_believe', 'ingredients', 'how_to'],
          additionalProperties: false,
        },
      },
    });

    const data = parseJsonContent(response);
    if (!data || !data.name) return null;
    return data;
  } catch (e) {
    console.warn('extractProductBrief failed; infographics will use the un-grounded prompt', e);
    return null;
  }
}

/**
 * Emiliano's per-image analysis. Given the target product image (baseProductUrl)
 * and a reference scene (referenceUrl), returns the /networks/design payload that
 * places the product into the scene.
 *
 * Returns { image_prompt, product_images_names, reference_images_urls }.
 * Falls back to a generic "replace product in scene" prompt if analysis fails.
 */
export async function buildReferenceDesignPayload({ baseProductUrl, referenceUrl, userInstructions = null, overlayText = null, overlayTextMode = 'editable', brandLogoUrl = null, language = null, extraProductUrls = [] }) {
  const extra = (userInstructions && String(userInstructions).trim())
    ? ` Additional user instruction: ${String(userInstructions).trim()}.`
    : '';

  const overlayExtra = (overlayTextMode === 'part_of_image' && overlayText && String(overlayText).trim())
    ? ` Overlay text to render in the image: "${String(overlayText).trim()}".`
    : '';

  const logoExtra = (brandLogoUrl && String(brandLogoUrl).trim())
    ? ` A brand logo has been provided as a dedicated BRAND LOGO IMAGE reference — incorporate it prominently in the design. Use ONLY that logo graphic; do NOT substitute it with any logo from the product packaging or from training knowledge.`
    : '';

  // The generated copy must read in the ORIGINAL product's language (from its
  // Amazon marketplace, e.g. amazon.com.mx → Spanish), even when styled after a
  // reference in another language. Reference infographics preserve their headlines
  // verbatim, so this MUST override that and translate ALL visible text — not just
  // the product-specific strings the analyzer swaps. (POR-13612-3)
  const lang = (language && String(language).trim()) ? String(language).trim() : '';
  const langExtra = lang
    ? ` CRITICAL LANGUAGE REQUIREMENT: the final product is sold in a ${lang}-language market. EVERY piece of visible text in the output image — headlines, callouts, badges, labels, feature lists, benefit claims, and any product text — MUST be written in ${lang}. Translate ALL text from the reference into ${lang}; do NOT leave any text in the reference's original language. Keep the layout, panels, structure, colors, and design identical — only the language of the text changes.`
    : '';
  // Also translate the analyzer's "new text" values so Case B's explicit
  // "change X to Y" swaps already carry the target language. (POR-13612-3)
  const langAnalysis = lang
    ? `\n\nIMPORTANT: The base product is sold in a ${lang}-language market. Write the NEW text values (text_B and text_D) in ${lang} — translate them into ${lang} even when the reference text is in another language.`
    : '';

  // Additional product image(s) — e.g. a cross-sell partner named by ASIN in the
  // regenerate prompt — provided as extra product reference images so both products
  // appear together instead of the base product being duplicated. (POR-13612-5)
  const extraProducts = (extraProductUrls || []).filter(Boolean);
  const multipleExtra = extraProducts.length > 1;
  const productExtra = extraProducts.length
    ? ` ADDITIONAL PRODUCT${multipleExtra ? 'S' : ''}: besides the base product, ${multipleExtra ? 'additional products have' : 'an additional product has'} been provided as extra product reference image${multipleExtra ? 's' : ''}. The output MUST show the base product AND ${multipleExtra ? 'each additional product' : 'the additional product'} together in the same composition (for a cross-sell, place them side by side, similarly sized), each rendered accurately and photorealistically from its OWN reference image with its packaging, branding, and label text kept 100% intact. Do NOT duplicate the base product in place of the additional product${multipleExtra ? 's' : ''}, and do NOT omit any product.`
    : '';

  const logoUrls = brandLogoUrl ? [brandLogoUrl] : [];

  const fallback = () => ({
    image_prompt: 'Recreate the first image\'s composition, layout, panels, tables, charts, badges, and text structure, but adapt it to the product shown in the second image. If the first image features a product, replace it with the product from the second image while keeping that product\'s design, branding, and text 100% intact and photorealistic. Update only product-specific text and imagery to match the new product; keep informational elements (nutrition tables, ingredient lists, step diagrams, callouts) intact in structure. Do not blend, ghost, or distort the product\'s identity.' + extra + overlayExtra + logoExtra + langExtra + productExtra,
    reference_images_urls: baseProductUrl ? [referenceUrl, baseProductUrl, ...extraProducts, ...logoUrls] : [referenceUrl, ...extraProducts, ...logoUrls],
    product_images_names: baseProductUrl ? [baseProductUrl, ...extraProducts] : [...extraProducts],
  });

  if (!baseProductUrl) return fallback();

  try {
    const [inlinedBase, inlinedRef] = await Promise.all([
      toOpenAiImageUrl(baseProductUrl),
      toOpenAiImageUrl(referenceUrl),
    ]);
    const response = await callOpenAiProxy({
      model: 'gpt-4o',
      messages: [
        {
          role: 'system',
          content: `Analyze the provided images to extract parameters for a design template.
- BASE PRODUCT: The product to be placed in the scene.
- REFERENCE SCENE: The background/environment.

You must categorize the situation into one of two cases:
Case A: The scene contains a product that should be replaced by the base product.
Case B: The scene does NOT contain a product to replace, or you are just editing scene elements.
If the reference is an infographic, label, nutrition/ingredients panel, table, chart, or text-heavy layout with no real product photo to swap, choose Case B and preserve its layout — update only product-specific text.

Identify:
1. Which case applies (A or B).
2. Text visible in the scene that should be updated to match the NEW product (e.g., if the scene says "Orange juice" but the base product is "Milk", update it). Provide "from" (original) and "to" (new).
3. A cohesive color palette "Z".${langAnalysis}

Return ONLY a JSON object:
{
  "case": "A" | "B",
  "text_A": "original 1",
  "text_B": "new 1",
  "text_C": "original 2",
  "text_D": "new 2",
  "palette_Z": "color palette"
}`,
        },
        {
          role: 'user',
          content: [
            { type: 'text', text: 'Analyze these images:' },
            { type: 'image_url', image_url: { url: inlinedBase } },
            { type: 'image_url', image_url: { url: inlinedRef } },
          ],
        },
      ],
      response_format: {
        name: 'design_parameters',
        strict: true,
        schema: {
          type: 'object',
          properties: {
            case: { type: 'string', enum: ['A', 'B'] },
            text_A: { type: 'string' },
            text_B: { type: 'string' },
            text_C: { type: 'string' },
            text_D: { type: 'string' },
            palette_Z: { type: 'string' },
          },
          required: ['case', 'text_A', 'text_B', 'text_C', 'text_D', 'palette_Z'],
          additionalProperties: false,
        },
      },
    });

    const data = parseJsonContent(response);
    if (!data) return fallback();

    // The analyzer returns PLACEHOLDER tokens (e.g. "[New Product Name]", "New Product
    // Claim") for text_B / text_D when it can't identify a genuinely DIFFERENT product to
    // swap to — which happens when the base product and the reference are the SAME or
    // similar product (e.g. an A/B hero test that uses the same hero as both Source and
    // per-variant reference). Injecting those placeholders into the Gemini prompt tells it
    // to rename/replace the product with nothing concrete, so it invents an unrelated
    // product — the reported "Batiste dry shampoo → orange juice" hallucination, cued by
    // whatever noun sits in the user's direction (e.g. "ORANGE"). Detect the placeholders
    // and DROP the corresponding "change X to Y" clause so the product's real identity is
    // preserved. Legit reference swaps (a real "Orange Juice" → "Milk") return concrete
    // strings and are unaffected.
    const isPlaceholderText = (s) => {
      const t = String(s || '').trim();
      if (!t) return true;
      if (/[[\]]/.test(t)) return true; // any square-bracket token, e.g. [New Product Name]
      return /\bnew\s+(product|brand|item|claim|name)\b/i.test(t)
        || /^(product|brand)\s+(name|claim)$/i.test(t);
    };
    const swap = (from, to) => (
      !isPlaceholderText(from) && !isPlaceholderText(to) && String(from).trim() !== String(to).trim()
    ) ? { from: String(from).trim(), to: String(to).trim() } : null;

    if (data.case === 'A') {
      const rename = swap(data.text_A, data.text_B);
      const renameSentence = rename
        ? ` If there is text in the scene referring to the old product, replace it with text relevant to the new product (e.g. change "${rename.from}" to "${rename.to}").`
        : '';
      return {
        image_prompt: `Edit the first image image. COMPLETELY REMOVE the existing product from the first image and replace it with the product from the second image, ensuring its visual integrity is maintained 100% without any modifications to its design, branding, or text.${renameSentence} The new product must be perfectly sharp, detailed, and high-resolution, looking like a real photograph integrated naturally into the environment. Do not allow any blending, ghosting, or distortion of the product's identity. Preserve the overall composition, background, and any graphics or text not tied to the old product. Use a "${data.palette_Z}" color palette.${extra}${overlayExtra}${logoExtra}${langExtra}${productExtra}`,
        reference_images_urls: [referenceUrl, baseProductUrl, ...extraProducts, ...logoUrls],
        product_images_names: [baseProductUrl, ...extraProducts],
      };
    }
    const swaps = [swap(data.text_A, data.text_B), swap(data.text_C, data.text_D)].filter(Boolean);
    const changeSentence = swaps.length
      ? `${swaps.map((s) => `Change text "${s.from}" to "${s.to}"`).join(', ')}. `
      : '';
    return {
      image_prompt: `Edit the second image image. ${changeSentence}Preserve the layout, panels, tables, charts, and overall structure of the image; only update product-specific text and imagery to match the new product. Use a "${data.palette_Z}" color palette.${extra}${overlayExtra}${logoExtra}${langExtra}${productExtra}`,
      reference_images_urls: [baseProductUrl, referenceUrl, ...extraProducts, ...logoUrls],
      product_images_names: [baseProductUrl, ...extraProducts],
    };
  } catch (e) {
    console.warn('buildReferenceDesignPayload analysis failed; using fallback prompt', e);
    return fallback();
  }
}
